import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useIntegrations } from "../../../hooks/useIntegrations";
import IntegrationTable from "../../../components/IntegrationTable";
import AddIntegrationForm from "../../../components/AddIntegrationForm";
import { usePageContext } from "../../../context/PageContext";
import { fetchData, testPassagensConnection } from "../../../api/client";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";

const PASSAGENS_KEYS = [
  "parceiropassagensrodoviarias",
  "passagens_integracoes",
  "passagens_totalbus_host",
  "passagens_totalbus_user",
  "passagens_totalbus_password",
  "passagens_totalbus_x_tenant_id",
  "passagens_totalbus_id_forma_pagamento",
  "passagens_totalbus_nome_forma_pagamento",
  "passagens_rodosoft_url",
  "passagens_rodosoft_token",
  "passagens_praxxio_url",
  "passagens_praxxio_token",
  "passagens_distribusion_url",
  "passagens_distribusion_token",
  "passagens_gopass_url",
  "passagens_gopass_token",
  "passagens_buson_url",
  "passagens_buson_token",
  "passagens_rodoviariaonline_url",
  "passagens_rodoviariaonline_token",
  "passagens_ura_integration_url",
  "passagens_ura_integration_token",
  "passagens_ura_use_local_fallback",
];

const defaultPassagensForm = {
  parceiro_padrao: "totalbus",
  totalbus_host: "",
  totalbus_user: "",
  totalbus_password: "",
  totalbus_x_tenant_id: "",
  totalbus_id_forma_pagamento: "",
  totalbus_nome_forma_pagamento: "",
  rodosoft_url: "",
  rodosoft_token: "",
  praxxio_url: "",
  praxxio_token: "",
  distribusion_url: "",
  distribusion_token: "",
  gopass_url: "",
  gopass_token: "",
  buson_url: "",
  buson_token: "",
  rodoviariaonline_url: "",
  rodoviariaonline_token: "",
  ura_integration_url: "",
  ura_integration_token: "",
  ura_use_local_fallback: true,
};

const Integrations = () => {
  const { t } = useTranslation("settings");
  const { userData } = useAuthContext();
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [passagensForm, setPassagensForm] = useState(defaultPassagensForm);
  const [passagensLoading, setPassagensLoading] = useState(false);
  const [passagensSaving, setPassagensSaving] = useState(false);
  const [passagensTesting, setPassagensTesting] = useState("");
  const [passagensMessage, setPassagensMessage] = useState(null);
  const [passagensTestStatus, setPassagensTestStatus] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    testMutation,
    isLoading,
  } = useIntegrations(currentPage, pageSize, search);

  useEffect(() => {
    if (error) setErrorMessage(t("integrationsPage.loadError"));
    else setErrorMessage(null);
  }, [error, t]);

  useEffect(() => {
    const loadPassagensConfig = async () => {
      try {
        setPassagensLoading(true);
        const qs = encodeURIComponent(PASSAGENS_KEYS.join(","));
        const res = await fetchData(`/configurations?keys=${qs}`, "GET");
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        const byKey = items.reduce((acc, item) => {
          acc[item.key] = item.value;
          return acc;
        }, {});
        setPassagensForm((prev) => ({
          ...prev,
          parceiro_padrao: byKey.parceiropassagensrodoviarias || prev.parceiro_padrao,
          totalbus_host: byKey.passagens_totalbus_host || "",
          totalbus_user: byKey.passagens_totalbus_user || "",
          totalbus_password: byKey.passagens_totalbus_password || "",
          totalbus_x_tenant_id: byKey.passagens_totalbus_x_tenant_id || "",
          totalbus_id_forma_pagamento: byKey.passagens_totalbus_id_forma_pagamento || "",
          totalbus_nome_forma_pagamento: byKey.passagens_totalbus_nome_forma_pagamento || "",
          rodosoft_url: byKey.passagens_rodosoft_url || "",
          rodosoft_token: byKey.passagens_rodosoft_token || "",
          praxxio_url: byKey.passagens_praxxio_url || "",
          praxxio_token: byKey.passagens_praxxio_token || "",
          distribusion_url: byKey.passagens_distribusion_url || "",
          distribusion_token: byKey.passagens_distribusion_token || "",
          gopass_url: byKey.passagens_gopass_url || "",
          gopass_token: byKey.passagens_gopass_token || "",
          buson_url: byKey.passagens_buson_url || "",
          buson_token: byKey.passagens_buson_token || "",
          rodoviariaonline_url: byKey.passagens_rodoviariaonline_url || "",
          rodoviariaonline_token: byKey.passagens_rodoviariaonline_token || "",
          ura_integration_url: byKey.passagens_ura_integration_url || "",
          ura_integration_token: byKey.passagens_ura_integration_token || "",
          ura_use_local_fallback: String(byKey.passagens_ura_use_local_fallback || "1") === "1",
        }));
      } catch (err) {
        setPassagensMessage({
          type: "error",
          text: err?.response?.data?.message || t("integrationsPage.loadTicketSettingsError"),
        });
      } finally {
        setPassagensLoading(false);
      }
    };
    loadPassagensConfig();
  }, [t]);

  useEffect(() => {
    const company = localStorage.getItem("company") || userData?.company || "default";
    const key = `passagens-test-status:${company}`;
    try {
      const raw = localStorage.getItem(key);
      setPassagensTestStatus(raw ? JSON.parse(raw) : {});
    } catch (_) {
      setPassagensTestStatus({});
    }
  }, [userData?.company]);

  /* ===== Handlers Linha ===== */
  const handleEdit = (integration) => {
    setSelectedIntegration(integration);
    setErrorMessage(null);
  };

  const handleDelete = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteIntegration = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err?.response?.data?.message || "Erro ao excluir a integração.");
      },
      onSettled: () => setIsProcessing(false),
    });
  };

  const toggleStatus = (row) => {
    setIsProcessing(true);
    updateMutation.mutate(
      { id: row.id, status: row.status ? 0 : 1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao alterar status da integração."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const testConnection = async (row) => {
    try {
      setIsProcessing(true);
      await testMutation.mutateAsync(row?.id);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Falha ao testar integração.");
    } finally {
      setIsProcessing(false);
    }
  };

  /* ===== Ações em massa ===== */
  const bulkActivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 1 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao ativar integrações.");
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDeactivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 0 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao desativar integrações.");
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => deleteMutation.mutateAsync(id)));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao excluir integrações.");
    } finally {
      setIsProcessing(false);
    }
  };

  /* ===== Paginação/Busca/Form ===== */
  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => {
    setSearch(term);
    refetch();
  };
  const handleCloseForm = () => {
    setSelectedIntegration(null);
    setErrorMessage(null);
  };

  const savePassagensConfig = async () => {
    try {
      setPassagensSaving(true);
      setPassagensMessage(null);
      const updates = [
        ["parceiropassagensrodoviarias", passagensForm.parceiro_padrao],
        ["passagens_totalbus_host", passagensForm.totalbus_host],
        ["passagens_totalbus_user", passagensForm.totalbus_user],
        ["passagens_totalbus_password", passagensForm.totalbus_password],
        ["passagens_totalbus_x_tenant_id", passagensForm.totalbus_x_tenant_id],
        ["passagens_totalbus_id_forma_pagamento", passagensForm.totalbus_id_forma_pagamento],
        ["passagens_totalbus_nome_forma_pagamento", passagensForm.totalbus_nome_forma_pagamento],
        ["passagens_rodosoft_url", passagensForm.rodosoft_url],
        ["passagens_rodosoft_token", passagensForm.rodosoft_token],
        ["passagens_praxxio_url", passagensForm.praxxio_url],
        ["passagens_praxxio_token", passagensForm.praxxio_token],
        ["passagens_distribusion_url", passagensForm.distribusion_url],
        ["passagens_distribusion_token", passagensForm.distribusion_token],
        ["passagens_gopass_url", passagensForm.gopass_url],
        ["passagens_gopass_token", passagensForm.gopass_token],
        ["passagens_buson_url", passagensForm.buson_url],
        ["passagens_buson_token", passagensForm.buson_token],
        ["passagens_rodoviariaonline_url", passagensForm.rodoviariaonline_url],
        ["passagens_rodoviariaonline_token", passagensForm.rodoviariaonline_token],
        ["passagens_ura_integration_url", passagensForm.ura_integration_url],
        ["passagens_ura_integration_token", passagensForm.ura_integration_token],
        ["passagens_ura_use_local_fallback", passagensForm.ura_use_local_fallback ? "1" : "0"],
      ];
      await Promise.all(
        updates.map(([key, value]) =>
          fetchData(`/configurations/${key}`, "PUT", { value })
        )
      );
      const passagensIntegracoes = {
        partnerDefault: passagensForm.parceiro_padrao,
        totalbus: {
          host: passagensForm.totalbus_host,
          user: passagensForm.totalbus_user,
          password: passagensForm.totalbus_password,
          xTenantId: passagensForm.totalbus_x_tenant_id,
          idFormaPagamento: passagensForm.totalbus_id_forma_pagamento,
          nomeFormaPagamento: passagensForm.totalbus_nome_forma_pagamento,
        },
        partners: {
          rodosoft: { url: passagensForm.rodosoft_url, token: passagensForm.rodosoft_token },
          praxxio: { url: passagensForm.praxxio_url, token: passagensForm.praxxio_token },
          distribusion: { url: passagensForm.distribusion_url, token: passagensForm.distribusion_token },
          gopass: { url: passagensForm.gopass_url, token: passagensForm.gopass_token },
          buson: { url: passagensForm.buson_url, token: passagensForm.buson_token },
          rodoviariaonline: { url: passagensForm.rodoviariaonline_url, token: passagensForm.rodoviariaonline_token },
        },
        ura: {
          integrationUrl: passagensForm.ura_integration_url,
          integrationToken: passagensForm.ura_integration_token,
          useLocalFallback: passagensForm.ura_use_local_fallback,
        },
      };
      await fetchData("/configurations/passagens_integracoes", "PUT", {
        value: JSON.stringify(passagensIntegracoes),
      });
      setPassagensMessage({
        type: "success",
        text: "Configurações de passagens salvas para este workspace.",
      });
    } catch (err) {
      setPassagensMessage({
        type: "error",
        text: err?.response?.data?.message || "Erro ao salvar configurações de passagens.",
      });
    } finally {
      setPassagensSaving(false);
    }
  };

  const runPassagensTest = async (target) => {
    const company = localStorage.getItem("company") || userData?.company || "default";
    const storageKey = `passagens-test-status:${company}`;
    try {
      setPassagensTesting(target);
      setPassagensMessage(null);
      const res = await testPassagensConnection(target);
      const next = {
        ...passagensTestStatus,
        [target]: {
          ok: true,
          message: res?.message || "Conexão OK.",
          at: new Date().toISOString(),
        },
      };
      setPassagensTestStatus(next);
      localStorage.setItem(storageKey, JSON.stringify(next));
      setPassagensMessage({
        type: "success",
        text: `${target}: ${res?.message || "Conexão OK."}`,
      });
    } catch (err) {
      const next = {
        ...passagensTestStatus,
        [target]: {
          ok: false,
          message: err?.message || err?.response?.data?.message || `Falha no teste de ${target}.`,
          at: new Date().toISOString(),
        },
      };
      setPassagensTestStatus(next);
      localStorage.setItem(storageKey, JSON.stringify(next));
      setPassagensMessage({
        type: "error",
        text: err?.message || err?.response?.data?.message || `Falha no teste de ${target}.`,
      });
    } finally {
      setPassagensTesting("");
    }
  };

  const statusChip = (target) => {
    const st = passagensTestStatus?.[target];
    if (!st) return <Chip size="small" label="Não testado" variant="outlined" />;
    const date = st.at ? new Date(st.at).toLocaleString() : "";
    return (
      <Chip
        size="small"
        color={st.ok ? "success" : "error"}
        label={st.ok ? `OK ${date}` : `Falha ${date}`}
        title={st.message || ""}
      />
    );
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("integrationsPage.title")}</Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedIntegration({})}
        >
          {t("integrationsPage.addIntegration")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedIntegration ? (
        <AddIntegrationForm
          data={selectedIntegration}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(payload) => {
            setIsProcessing(true);
            updateMutation.mutate(payload, {
              onSuccess: () => { refetch(); handleCloseForm(); },
              onError: (err) =>
                setErrorMessage(err?.response?.data?.message || "Erro ao salvar a integração."),
              onSettled: () => setIsProcessing(false),
            });
          }}
          onTest={async (payloadOrId) => {
            try {
              setIsProcessing(true);
              // se ainda não existe, primeiro cria/atualiza
              let id = payloadOrId?.id;
              if (!id && payloadOrId) {
                const res = await updateMutation.mutateAsync(payloadOrId);
                id = res?.data?.id || res?.id;
              }
              if (!id) throw new Error("Integração sem ID para testar.");
              await testMutation.mutateAsync(id);
            } catch (err) {
              setErrorMessage(err?.response?.data?.message || err?.message || "Falha ao testar integração.");
            } finally {
              setIsProcessing(false);
            }
          }}
        />
      ) : (
        <>
          <IntegrationTable
            integrations={data?.integrations || []}
            rowCount={data?.pagination?.total || 0}
            pageSize={pageSize}
            page={currentPage - 1}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            isLoading={isLoading || isProcessing}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleStatus={toggleStatus}
            onTest={testConnection}
            onBulkActivate={bulkActivate}
            onBulkDeactivate={bulkDeactivate}
            onBulkDelete={bulkDelete}
          />

          <Accordion sx={{ mt: 2 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{t("integrationsPage.accordionTicketsTitle")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {passagensMessage && (
                <Alert severity={passagensMessage.type} sx={{ mb: 2 }}>
                  {passagensMessage.text}
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label={t("integrationsPage.defaultPartner")}
                    value={passagensForm.parceiro_padrao}
                    onChange={(e) =>
                      setPassagensForm((p) => ({ ...p, parceiro_padrao: e.target.value }))
                    }
                  >
                    {["totalbus", "rodosoft", "praxxio", "distribusion", "gopass", "buson", "rodoviariaonline"].map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={8}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!passagensForm.ura_use_local_fallback}
                        onChange={(e) =>
                          setPassagensForm((p) => ({ ...p, ura_use_local_fallback: e.target.checked }))
                        }
                      />
                    }
                    label={t("integrationsPage.ivrLocalFallback")}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Totalbus</Typography>
              <Grid container spacing={2}>
                {[
                  ["totalbus_host", "Host"],
                  ["totalbus_user", "Usuário"],
                  ["totalbus_password", "Senha"],
                  ["totalbus_x_tenant_id", "X Tenant ID"],
                  ["totalbus_id_forma_pagamento", "ID Forma Pagamento"],
                  ["totalbus_nome_forma_pagamento", "Nome Forma Pagamento"],
                ].map(([key, label]) => (
                  <Grid item xs={12} md={4} key={key}>
                    <TextField
                      fullWidth
                      type={String(key).includes("password") ? "password" : "text"}
                      label={label}
                      value={passagensForm[key] || ""}
                      onChange={(e) =>
                        setPassagensForm((p) => ({ ...p, [key]: e.target.value }))
                      }
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      onClick={() => runPassagensTest("totalbus")}
                      disabled={!!passagensTesting || passagensLoading}
                    >
                      {passagensTesting === "totalbus" ? "Testando Totalbus..." : "Testar conexão Totalbus"}
                    </Button>
                    {statusChip("totalbus")}
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{t("integrationsPage.partnersViaHttp")}</Typography>
              <Grid container spacing={2}>
                {[
                  ["rodosoft", "Rodosoft"],
                  ["praxxio", "Praxxio"],
                  ["distribusion", "Distribusion"],
                  ["gopass", "Gopass"],
                  ["buson", "Buson"],
                  ["rodoviariaonline", "Rodoviaria Online"],
                ].map(([partnerKey, label]) => (
                  <React.Fragment key={partnerKey}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={`${label} URL`}
                        value={passagensForm[`${partnerKey}_url`] || ""}
                        onChange={(e) =>
                          setPassagensForm((p) => ({ ...p, [`${partnerKey}_url`]: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="password"
                        label={`${label} Token`}
                        value={passagensForm[`${partnerKey}_token`] || ""}
                        onChange={(e) =>
                          setPassagensForm((p) => ({ ...p, [`${partnerKey}_token`]: e.target.value }))
                        }
                      />
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={12} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {["rodosoft", "praxxio", "distribusion", "gopass", "buson", "rodoviariaonline"].map((partner) => (
                    <Box key={partner} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => runPassagensTest(partner)}
                        disabled={!!passagensTesting || passagensLoading}
                      >
                        {passagensTesting === partner ? `Testando ${partner}...` : `Testar ${partner}`}
                      </Button>
                      {statusChip(partner)}
                    </Box>
                  ))}
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{t("integrationsPage.externalIvrGateway")}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="URL integração URA passagens"
                    value={passagensForm.ura_integration_url}
                    onChange={(e) =>
                      setPassagensForm((p) => ({ ...p, ura_integration_url: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Token integração URA passagens"
                    value={passagensForm.ura_integration_token}
                    onChange={(e) =>
                      setPassagensForm((p) => ({ ...p, ura_integration_token: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      onClick={() => runPassagensTest("ura_gateway")}
                      disabled={!!passagensTesting || passagensLoading}
                    >
                      {passagensTesting === "ura_gateway" ? "Testando gateway URA..." : "Testar gateway URA"}
                    </Button>
                    {statusChip("ura_gateway")}
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={savePassagensConfig}
                  disabled={passagensLoading || passagensSaving}
                >
                  {passagensSaving ? t("integrationsPage.saving") : t("integrationsPage.saveTicketsSettings")}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("shared.deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("shared.processingDelete") : t("integrationsPage.deleteIntegrationQuestion")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteIntegration}>{t("shared.delete")}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Integrations;
